import { useState, useMemo, useEffect, useRef, Fragment } from 'react'
import { useAuth } from '../hooks/useAuth'
import { updateAircraft, deleteSubtask } from '../lib/api'
import { logChange } from '../lib/auditLog'
import { createPortal } from 'react-dom'
import { DEPT_COLORS, DEPT_PRIORITY } from '../lib/deptColors'
import DayPickerPopover from './DayPickerPopover'
import SubtaskModal from './SubtaskModal'
import ExportButton from './ExportButton'
import { registrationColor } from '../lib/constants'
import { getUSHolidays, isOffDay } from '../lib/businessDays'
import { QA_MILESTONE_NAMES } from '../lib/qaMilestones'

// ── UTILITIES ─────────────────────────────────────────────────────────────────

function addDays(date, n) {
  const d = new Date(date); d.setDate(d.getDate() + n); return d
}

function toISO(date) { return date.toISOString().slice(0, 10) }

function fmtMd(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function fmtDow(date) {
  return date.toLocaleDateString('en-US', { weekday: 'short' })
}

function buildChunks(startDate, endDate) {
  const chunks = []
  let cs = new Date(startDate); cs.setHours(0, 0, 0, 0)
  const end = new Date(endDate); end.setHours(0, 0, 0, 0)
  while (cs <= end) {
    chunks.push(Array.from({ length: 14 }, (_, i) => {
      const d = new Date(cs.getTime() + i * 86400000)
      d.setHours(0, 0, 0, 0)
      return d
    }))
    const next = new Date(cs)
    next.setDate(next.getDate() + 14) // use setDate (local calendar) to avoid DST fallback underflow
    next.setHours(0, 0, 0, 0)
    cs = next
  }
  return chunks
}

// ── SORTING ───────────────────────────────────────────────────────────────────

function assignBars(items, chunkDays) {
  const chunkISOs = chunkDays.map(d => toISO(d))
  const chunkStart = chunkISOs[0]
  const chunkEnd = chunkISOs[chunkISOs.length - 1]
  const week1Start = chunkISOs[0]
  const week1End = chunkISOs[6]
  const week2Start = chunkISOs[7]
  const week2End = chunkISOs[13]
  const bars = []

  items.forEach(item => {
    const itemStart = item.start_date || ''
    const itemEnd = item.end_date || '9999-12-31'
    if (itemEnd < chunkStart || itemStart > chunkEnd) return

    const startIdx = itemStart < chunkStart
      ? 0
      : chunkISOs.findIndex(iso => iso >= itemStart)
    const endIdx = itemEnd > chunkEnd
      ? chunkISOs.length - 1
      : chunkISOs.reduce((last, iso, i) => iso <= itemEnd ? i : last, -1)
    if (startIdx === -1 || endIdx === -1 || startIdx > endIdx) return

    // Sort span: use only dept subtasks that overlap this chunk so a later-chunk
    // milestone doesn't inflate the sort position in an earlier chunk.
    let sortStart = itemStart
    let sortEnd   = itemEnd
    if (item._deptSubtasks && item._deptSubtasks.length > 0) {
      const chunkSubs = item._deptSubtasks.filter(s =>
        s.start_date && s.end_date &&
        s.end_date >= chunkStart && s.start_date <= chunkEnd
      )
      if (chunkSubs.length > 0) {
        sortStart = chunkSubs.reduce((min, s) => s.start_date < min ? s.start_date : min, chunkSubs[0].start_date)
        sortEnd   = chunkSubs.reduce((max, s) => s.end_date   > max ? s.end_date   : max, chunkSubs[0].end_date)
      }
    }
    bars.push({
      item,
      startCol: startIdx,
      endCol: endIdx,
      span: endIdx - startIdx + 1,
      _actualStart: itemStart,
      _actualEnd: itemEnd,
      _sortStart: sortStart,
      _sortEnd: sortEnd,
    })
  })

  const isFullWeek = (bar) => {
    const spansWeek1 = bar._sortStart <= week1Start && bar._sortEnd >= week1End
    const spansWeek2 = bar._sortStart <= week2Start && bar._sortEnd >= week2End
    return spansWeek1 && spansWeek2
  }

  bars.sort((a, b) => {
    const aFull = isFullWeek(a)
    const bFull = isFullWeek(b)
    if (aFull && !bFull) return 1
    if (!aFull && bFull) return -1
    if (aFull && bFull) return a._sortEnd.localeCompare(b._sortEnd)
    const aEndsInChunk = a._sortEnd <= chunkEnd
    const bEndsInChunk = b._sortEnd <= chunkEnd
    if (aEndsInChunk && !bEndsInChunk) return -1
    if (!aEndsInChunk && bEndsInChunk) return 1
    if (aEndsInChunk && bEndsInChunk) return a._sortEnd.localeCompare(b._sortEnd)
    return b._sortEnd.localeCompare(a._sortEnd)
  })

  // Group bars from the same project into adjacent rows, using first-appearance order for groups
  const projectOrder = []
  const projectGroups = {}
  for (const bar of bars) {
    const pid = bar.item.project_id
    if (!projectGroups[pid]) {
      projectGroups[pid] = []
      projectOrder.push(pid)
    }
    projectGroups[pid].push(bar)
  }
  const reordered = projectOrder.flatMap(pid => projectGroups[pid])
  return reordered.map((bar, i) => ({ ...bar, row: i }))
}

// ── QA VIEW ───────────────────────────────────────────────────────────────────

const URGENCY_STRIPE = {
  active: '#3b82f6',
  red:    '#ef4444',
  orange: '#f97316',
  yellow: '#eab308',
  gray:   '#9ca3af',
  done:   '#d1d5db',
}

function getUrgencyTier(startDate, endDate, todayISO) {
  if (!startDate) return 'gray'
  if (endDate && endDate < todayISO) return 'done'
  if (startDate <= todayISO) return 'active'
  const daysUntil = Math.round(
    (new Date(startDate + 'T00:00:00') - new Date(todayISO + 'T00:00:00')) / 86400000
  )
  if (daysUntil <= 1) return 'red'
  if (daysUntil <= 3) return 'orange'
  if (daysUntil <= 7) return 'yellow'
  return 'gray'
}

function buildQAItems(projects, subtasks) {
  const todayISO = toISO(new Date())
  const items = []
  for (const project of projects) {
    const reg = project.aircraft?.registration || '—'
    const color = registrationColor(reg, project.aircraft?.color || null)
    const qaSubs = subtasks.filter(s =>
      s.project_id === project.id &&
      subtaskDepts(s).includes('QA') &&
      s.start_date && s.end_date
    )
    for (const sub of qaSubs) {
      const tier = sub.status === 'Complete' ? 'done' : getUrgencyTier(sub.start_date, sub.end_date, todayISO)
      items.push({
        id: sub.id,
        project_id: project.id,
        start_date: sub.start_date,
        end_date: sub.end_date,
        department: 'QA',
        status: sub.status,
        task_name: sub.task_name,
        _registration: reg,
        _label: sub.task_name,
        _color: color,
        _urgencyTier: tier,
        _isQARow: true,
        _isProjectBar: false,
        _isLeadDept: true,
        _aircraftId: project.aircraft?.id,
      })
    }
  }
  // Active rows first (sorted by end_date — soonest ending = most urgent),
  // then upcoming rows sorted by start_date, then done rows last.
  items.sort((a, b) => {
    const tierOrder = { active: 0, red: 1, orange: 1, yellow: 1, gray: 1, done: 2 }
    const ao = tierOrder[a._urgencyTier] ?? 1
    const bo = tierOrder[b._urgencyTier] ?? 1
    if (ao !== bo) return ao - bo
    if (a._urgencyTier === 'active') return (a.end_date || '').localeCompare(b.end_date || '')
    return (a.start_date || '').localeCompare(b.start_date || '')
  })
  return items
}

function assignQABars(items, chunkDays) {
  const chunkISOs = chunkDays.map(d => toISO(d))
  const chunkStart = chunkISOs[0]
  const chunkEnd = chunkISOs[chunkISOs.length - 1]
  const bars = []
  for (const item of items) {
    const itemStart = item.start_date || ''
    const itemEnd = item.end_date || '9999-12-31'
    if (itemEnd < chunkStart || itemStart > chunkEnd) continue
    const startIdx = itemStart < chunkStart ? 0 : chunkISOs.findIndex(iso => iso >= itemStart)
    const endIdx = itemEnd > chunkEnd
      ? chunkISOs.length - 1
      : chunkISOs.reduce((last, iso, i) => iso <= itemEnd ? i : last, -1)
    if (startIdx === -1 || endIdx === -1 || startIdx > endIdx) continue
    bars.push({
      item,
      startCol: startIdx,
      endCol: endIdx,
      span: endIdx - startIdx + 1,
      _actualStart: itemStart,
      _actualEnd: itemEnd,
      row: bars.length,
    })
  }
  return bars
}

// ── MULTI-DEPT HELPER ─────────────────────────────────────────────────────────

function subtaskDepts(s) {
  return s.departments && s.departments.length > 0 ? s.departments : [s.department]
}

// Returns the highest-priority dept for color display (MX > AVI > INT > PA > …)
function primarySubtaskDept(s) {
  const depts = subtaskDepts(s)
  return DEPT_PRIORITY.find(d => depts.includes(d)) || depts[0]
}

// ── SEGMENT LOGIC ─────────────────────────────────────────────────────────────

// Active-dept subtasks are listed first so segments.find() gives them priority over other-dept
function buildPrioritySegments(deptSubtasks, otherDeptSubtasks, baseColor, orgDeptMap = {}) {
  const segs = []
  deptSubtasks.forEach(s => {
    if (!s.start_date || !s.end_date) return
    segs.push({ start: s.start_date, end: s.end_date, color: baseColor, deptColor: null, label: s.task_name, isSubtask: true, isDifferentDept: false })
  })
  otherDeptSubtasks.forEach(s => {
    if (!s.start_date || !s.end_date) return
    segs.push({ start: s.start_date, end: s.end_date, color: '#d1d5db', deptColor: orgDeptMap[primarySubtaskDept(s)]?.hex || '#9ca3af', label: s.task_name, isSubtask: true, isDifferentDept: true })
  })
  return segs
}

function buildDeptItems(dept, projects, subtasks, orgDeptMap = {}) {
  const items = []
  const isPiston = dept === 'PISTON'

  projects.forEach(project => {
    const reg = project.aircraft?.registration || '—'
    const model = project.aircraft?.model || null
    const aircraftType = project.aircraft?.aircraft_type || 'Jet'

    // PISTON tab: only piston aircraft
    if (isPiston && aircraftType !== 'Piston') return

    if (isPiston) {
      // Only show on the Piston tab when the project is purely MX work:
      //   - project_department must be absent or MX
      //   - every subtask (if any) must be MX
      // Any non-MX signal (lead dept or subtask) means it belongs on that dept's tab.
      const projectDeptNonMx = (project.departments || ['MX']).some(d => d !== 'MX')
      const projectSubtasks = subtasks.filter(s => s.project_id === project.id)
      const hasNonMxSubtask = projectSubtasks.some(s =>
        subtaskDepts(s).some(d => d !== 'MX') && !(subtaskDepts(s).includes('QA') && QA_MILESTONE_NAMES.has(s.task_name))
      )
      if (projectDeptNonMx || hasNonMxSubtask) return

      const color = registrationColor(reg, project.aircraft?.color || null)
      if (!project.departure_date) {
        items.push({
          id: project.id, project_id: project.id,
          start_date: new Date().toISOString().slice(0, 10),
          end_date: new Date().toISOString().slice(0, 10),
          department: 'PISTON', status: project.status,
          task_name: project.workscope || reg,
          _registration: reg, _label: project.workscope || '(no workscope)',
          _isPin: true, _isProjectBar: true, _color: color, _aircraftId: project.aircraft?.id,
          _segments: [],
        })
      } else {
        items.push({
          id: project.id, project_id: project.id,
          start_date: project.arrival_date, end_date: project.departure_date,
          department: 'PISTON', status: project.status,
          task_name: project.workscope || reg,
          _registration: reg, _label: project.workscope || '(no workscope)',
          _isPin: false, _isProjectBar: true, _color: color, _aircraftId: project.aircraft?.id,
          _segments: [],
        })
      }
      return
    }

    // MX tab: piston aircraft with MX-only work belong on the PISTON tab, not here
    if (dept === 'MX' && aircraftType === 'Piston') return

    const isLeadDept = (project.departments || []).includes(dept)
    const baseColor = registrationColor(reg, project.aircraft?.color || null)
    const allProjectSubtasks = subtasks.filter(s => s.project_id === project.id)
    const deptSubtasks = allProjectSubtasks
      .filter(s => subtaskDepts(s).includes(dept) && s.start_date && s.end_date)
      .sort((a, b) => (a.end_date || '').localeCompare(b.end_date || ''))
    const otherDeptSubtasks = allProjectSubtasks.filter(s =>
      !subtaskDepts(s).includes(dept) && s.start_date && s.end_date &&
      !(subtaskDepts(s).includes('QA') && QA_MILESTONE_NAMES.has(s.task_name))
    )

    if (!isLeadDept && deptSubtasks.length === 0) return

    const subtaskEarliestStart = deptSubtasks.reduce((min, s) => (!min || s.start_date < min) ? s.start_date : min, null)
    // Lead-dept: start from arrival (or earliest subtask, whichever is sooner)
    // Non-lead-dept: start exactly at first subtask (bar only covers the subtask windows)
    const startDate = isLeadDept
      ? ([project.arrival_date, subtaskEarliestStart].filter(Boolean).sort()[0] || null)
      : subtaskEarliestStart
    const deptLastSubtaskEnd = deptSubtasks.length > 0 ? deptSubtasks[deptSubtasks.length - 1].end_date : null
    const allDeptComplete = deptSubtasks.length > 0 && deptSubtasks.every(s => s.status === 'Complete')
    const endDate = (!allDeptComplete && project.departure_date) ? project.departure_date : (deptLastSubtaskEnd || project.departure_date || null)
    if (!startDate) return

    const segments = buildPrioritySegments(deptSubtasks, otherDeptSubtasks, baseColor, orgDeptMap)

    items.push({
      id: project.id, project_id: project.id,
      start_date: startDate,
      end_date: endDate || startDate,
      department: dept, status: project.status,
      task_name: project.workscope || reg,
      _registration: reg, _label: project.workscope || '(no workscope)',
      _isSubtask: false, _isProjectBar: true,
      _color: baseColor,
      _segments: segments,
      _deptSubtasks: deptSubtasks,
      _subtasks: allProjectSubtasks.map(s => ({ ...s, _registration: reg })),
      _hasExpand: deptSubtasks.length > 1,
      _isLeadDept: isLeadDept,
      _projectDept: (project.departments || ['MX'])[0],
      _aircraftId: project.aircraft?.id,
      _allDeptComplete: deptSubtasks.length > 0 && deptSubtasks.every(s => s.status === 'Complete'),
    })
  })

  return items
}

// ── AC TYPE ITEMS ─────────────────────────────────────────────────────────────

function buildAcTypeItems(projects, subtasks) {
  const items = []
  projects.forEach(project => {
    if (!project.arrival_date) return
    const reg = project.aircraft?.registration || '—'
    const color = registrationColor(reg, project.aircraft?.color || null)
    const allProjectSubtasks = subtasks.filter(s => s.project_id === project.id)
    items.push({
      id: project.id, project_id: project.id,
      start_date: project.arrival_date,
      end_date: project.departure_date || project.arrival_date,
      status: project.status,
      task_name: project.workscope || reg,
      _registration: reg, _label: project.workscope || '(no workscope)',
      _isSubtask: false, _isProjectBar: true,
      _color: color,
      _segments: [],
      _deptSubtasks: [],
      _subtasks: allProjectSubtasks.map(s => ({ ...s, _registration: reg })),
      _hasExpand: false,
      _isLeadDept: true,
      _aircraftId: project.aircraft?.id,
    })
  })
  return items
}

function AcTypeView({ projects, subtasks, onClickSubtask, onDayClick, expandAll, onColorChange }) {
  const todayISO = toISO(new Date())
  const { profile } = useAuth()
  const weekStartDay = profile?.orgs?.settings?.scheduling?.weekStartDay ?? 1

  const { pastChunks, currentChunk, futureChunks } = useMemo(() => {
    const jan1 = new Date(new Date().getFullYear(), 0, 1)
    jan1.setDate(jan1.getDate() - ((jan1.getDay() - weekStartDay + 7) % 7))
    const yearEnd = addDays(new Date(), 365)
    const all = buildChunks(jan1, yearEnd)
    return {
      pastChunks: all.filter(days => toISO(days[13]) < todayISO).slice(-3),
      currentChunk: all.find(days => toISO(days[0]) <= todayISO && toISO(days[13]) >= todayISO) || null,
      futureChunks: all.filter(days => toISO(days[0]) > todayISO),
    }
  }, [weekStartDay])

  const items = useMemo(() =>
    buildAcTypeItems(projects.filter(p => p.status !== 'TBD' && p.arrival_date), subtasks),
    [projects, subtasks]
  )

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
        No projects to display for the selected type.
      </div>
    )
  }

  return (
    <div id="dept-export">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {pastChunks.length > 0 && (
          <PastPeriodsGroup chunks={pastChunks} items={items} onClickSubtask={onClickSubtask} onDayClick={onDayClick}
            expandAll={expandAll} hideOtherDept={false} onColorChange={onColorChange} isQAView={false} />
        )}
        {currentChunk && (() => {
          const bars = assignBars(items, currentChunk)
          return (
            <DeptChunk chunkDays={currentChunk} bars={bars} onClickSubtask={onClickSubtask} onDayClick={onDayClick}
              items={items} expandAll={expandAll} hideOtherDept={false} onColorChange={onColorChange} isQAView={false} />
          )
        })()}
        {futureChunks.length > 0 && (
          <FuturePeriodsGroup chunks={futureChunks} items={items} onClickSubtask={onClickSubtask} onDayClick={onDayClick}
            expandAll={expandAll} hideOtherDept={false} onColorChange={onColorChange} isQAView={false} />
        )}
      </div>
    </div>
  )
}

// ── HANGAR ITEMS ─────────────────────────────────────────────────────────────

function buildHangarItems(hangars, projects, subtasks) {
  const items = []
  projects.forEach(project => {
    const norm = (project.hangar_location || '').trim().toUpperCase()
    if (!norm || !hangars.has(norm)) return
    const reg = project.aircraft?.registration || '—'
    const color = registrationColor(reg, project.aircraft?.color || null)
    const allProjectSubtasks = subtasks.filter(s => s.project_id === project.id)
    const scheduledSubtasks = allProjectSubtasks
      .filter(s => s.start_date && s.end_date && !(subtaskDepts(s).includes('QA') && QA_MILESTONE_NAMES.has(s.task_name)))
      .sort((a, b) => (a.end_date || '').localeCompare(b.end_date || ''))
    if (!project.arrival_date) return
    const segments = buildPrioritySegments(scheduledSubtasks, [], color)
    items.push({
      id: project.id, project_id: project.id,
      start_date: project.arrival_date,
      end_date: project.departure_date || project.arrival_date,
      status: project.status,
      task_name: project.workscope || reg,
      _registration: reg, _label: project.workscope || '(no workscope)',
      _isSubtask: false, _isProjectBar: true,
      _color: color,
      _segments: segments,
      _deptSubtasks: scheduledSubtasks,
      _subtasks: allProjectSubtasks.map(s => ({ ...s, _registration: reg })),
      _hasExpand: scheduledSubtasks.length > 1,
      _isLeadDept: true,
      _aircraftId: project.aircraft?.id,
    })
  })
  return items
}

// ── COLOR PICKER POPOVER ──────────────────────────────────────────────────────

// position = { top, right } in viewport px — supplied by the opener via getBoundingClientRect
function BarColorPicker({ currentColor, onSave, position }) {
  const [color, setColor] = useState(currentColor || '#3b82f6')
  const pickerWidth = 156
  // Flip to left-anchor when the button is too close to the left edge
  const spaceOnLeft = window.innerWidth - position.right // = rect.right
  const anchor = spaceOnLeft >= pickerWidth
    ? { right: position.right }
    : { left: Math.max(8, position.left) }

  return (
    <div
      className="fixed z-[9999] bg-white rounded-lg shadow-xl border border-gray-200 p-3"
      style={{
        top: position.top,
        ...anchor,
        transform: 'translateY(calc(-100% - 6px))',
        minWidth: pickerWidth,
      }}
      onClick={e => e.stopPropagation()}
    >
      <p className="text-xs font-semibold text-gray-600 mb-2">Bar color</p>
      <div className="flex items-center gap-2 mb-2.5">
        <input
          type="color" value={color} onChange={e => setColor(e.target.value)}
          className="w-9 h-9 rounded-lg cursor-pointer border border-gray-200 p-0.5"
        />
        <span className="text-xs text-gray-500 font-mono">{color}</span>
      </div>
      <div className="flex gap-1.5">
        <button
          onClick={() => onSave(color)}
          className="flex-1 text-xs bg-brand-red hover:bg-brand-red-dark text-white rounded-md px-2 py-1 font-medium transition-colors"
        >
          Save
        </button>
        <button
          onClick={() => onSave(null)}
          className="text-xs text-gray-500 hover:text-red-500 rounded-md px-2 py-1 border border-gray-200 hover:border-red-200 transition-colors"
        >
          Reset
        </button>
      </div>
    </div>
  )
}

// ── SEGMENTED BAR ─────────────────────────────────────────────────────────────

function ToggleChevron({ expanded, onToggle, dark }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onToggle() }}
      className="w-4 h-4 flex items-center justify-center flex-shrink-0 rounded-sm hover:bg-white/30 transition-colors"
    >
      <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`}
        fill="none" stroke="currentColor" viewBox="0 0 24 24"
        style={{ color: dark ? '#4b5563' : 'white' }}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  )
}

function SegmentedBar({ bar, colWidth, onClickSubtask, chunkDays, expanded, onToggle, hideOtherDept, hasExpand, onColorChange }) {
  const { item } = bar
  const segments = item._segments
  const baseColor = item._color || '#9ca3af'
  const isLeadDept = item._isLeadDept ?? true
  const effectiveHasExpand = hasExpand ?? item._hasExpand
  const isNarrow = bar.span === 1
  const labelOnRight = bar.startCol === 0
  const today = new Date().toISOString().slice(0, 10)
  const isOverdue = item.end_date && item.end_date < today && item.status !== 'Complete' && !item._allDeptComplete
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerPos, setPickerPos] = useState(null)

  const handleColorSave = async (color) => {
    setPickerOpen(false)
    if (onColorChange && item._aircraftId) await onColorChange(item._aircraftId, color)
  }

  const openPicker = (e) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    setPickerPos({ top: rect.top, left: rect.left, right: window.innerWidth - rect.right })
    setPickerOpen(true)
  }

  // External label shown outside bar for narrow bars
  const externalLabel = isNarrow ? (
    <div
      className="absolute top-0 bottom-0 flex items-center pointer-events-none z-20"
      style={{
        ...(labelOnRight
          ? { left: '100%', paddingLeft: 3 }
          : { right: '100%', paddingRight: 3, justifyContent: 'flex-end' }
        ),
        whiteSpace: 'nowrap',
      }}
    >
      <span className={`text-xs font-bold bg-white/90 px-1 rounded shadow-sm ${isOverdue ? 'text-red-600' : 'text-gray-700'}`}>
        {item._registration}
      </span>
    </div>
  ) : null

  // No chunkDays — plain bar (off-day logic requires chunkDays)
  if (!chunkDays || !segments) {
    return (
      <div
        className="absolute cursor-pointer hover:opacity-80 transition-opacity group"
        style={{
          left: `${bar.startCol * colWidth}%`,
          width: `${bar.span * colWidth}%`,
          top: bar.row * 32 + 4,
          height: 28,
          overflow: 'visible',
        }}
        onClick={() => onClickSubtask(item)}
      >
        {externalLabel}
        <div
          className="w-full h-full rounded flex items-center overflow-hidden px-1.5 gap-1"
          style={{ backgroundColor: baseColor }}
        >
          {effectiveHasExpand && !isNarrow && <ToggleChevron expanded={expanded} onToggle={onToggle} dark={false} />}
          {!isNarrow && (
            <span className={`text-xs font-bold flex-shrink-0 ${isOverdue ? 'text-red-300' : 'text-white'}`}
              style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
              {item._registration}
            </span>
          )}
          {bar.span > 1 && (
            <span className="text-xs text-white/90 truncate">{item._label}</span>
          )}
        </div>
        {onColorChange && item._aircraftId && (
          <div
            className="absolute right-1 top-0 bottom-0 flex items-center z-20 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={openPicker}
          >
            <div className="w-4 h-4 rounded-full border-2 border-white shadow cursor-pointer"
              style={{ backgroundColor: item._color || '#9ca3af' }} />
          </div>
        )}
        {pickerOpen && pickerPos && createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={e => { e.stopPropagation(); setPickerOpen(false) }} />
            <BarColorPicker currentColor={item._color} position={pickerPos} onSave={handleColorSave} />
          </>,
          document.body
        )}
      </div>
    )
  }

  const chunkISOs = chunkDays.map(d => toISO(d))

  const chunkStart = chunkISOs[0]
  const chunkEnd = chunkISOs[chunkISOs.length - 1]


  // For each column determine color/label (first pass — no off-day transparency yet)
  const rawColData = chunkISOs.map((iso, idx) => {
    const active = segments.find(s => s.start <= iso && s.end >= iso)
    // For non-lead-dept bars, skip other-dept hits so they fall through to the gap branch below
    if (active && (isLeadDept || !active.isDifferentDept)) {
      return {
        color: active.color,
        deptColor: active.deptColor,
        label: active.label,
        isSubtask: true,
        isDifferentDept: active.isDifferentDept,
        isOffDayGap: false,
      }
    }
    // Non-lead-dept projects: only show color during own-dept subtask windows — gaps are transparent
    if (!isLeadDept && segments.length > 0) {
      return { color: 'transparent', deptColor: null, label: null, isSubtask: false, isDifferentDept: false, isOffDayGap: true }
    }
    return {
      color: baseColor,
      deptColor: null,
      label: (isOffDay(chunkDays[idx]) && iso !== item.start_date && iso !== item.end_date && segments.length > 0) ? null : item._label,
      isSubtask: false,
      isDifferentDept: false,
      isOffDayGap: false,
    }
  })

  // Second pass: off-days (weekends/holidays) are transparent unless the project
  // starts/ends on that day, a subtask is actively scheduled on it, or the project
  // has no subtasks at all (in which case the bar spans continuously through weekends).
  const colData = rawColData.map((col, idx) => {
    if (!isOffDay(chunkDays[idx])) return col
    const iso = chunkISOs[idx]
    if (iso === item.start_date || iso === item.end_date) return col
    // For non-lead-dept, only own-dept segments keep off-days colored
    const segCovers = isLeadDept
      ? segments.some(s => s.start <= iso && s.end >= iso)
      : segments.some(s => !s.isDifferentDept && s.start <= iso && s.end >= iso)
    if (segCovers) return col
    if (segments.length === 0) return col
    return { color: 'transparent', deptColor: null, label: null, isSubtask: false, isDifferentDept: false, isOffDayGap: true }
  })

  // Collapse consecutive same-color columns into render segments
  const renderSegs = []
  let cur = null

  for (let i = bar.startCol; i <= bar.endCol; i++) {
    const d = colData[i]
    if (!cur || d.color !== cur.color || d.label !== cur.label) {
      if (cur) renderSegs.push(cur)
      cur = { startCol: i, endCol: i, ...d }
    } else {
      cur.endCol = i
    }
  }
  if (cur) renderSegs.push(cur)

  const firstVisibleSeg = hideOtherDept
    ? (renderSegs.find(s => !s.isDifferentDept && !s.isOffDayGap) ?? null)
    : (renderSegs.find(s => !s.isOffDayGap) ?? null)
  const firstVisibleIdx = firstVisibleSeg ? renderSegs.indexOf(firstVisibleSeg) : 0
  const firstVisibleCols = firstVisibleSeg ? firstVisibleSeg.endCol - firstVisibleSeg.startCol + 1 : 0
  const showSegExtReg = !isNarrow && firstVisibleCols === 1
  // Use an overlay for the registration when:
  // - the first colored segment starts late (mid-bar), OR
  // - the bar has an expand chevron (chevron + reg together overflow the narrow first segment)
  // The overlay lives outside the segments container so overflow:hidden can't clip it.
  const useRegOverlay = !isNarrow && !showSegExtReg && !!firstVisibleSeg &&
    (firstVisibleSeg.startCol > bar.startCol || effectiveHasExpand)
  // Dynamic padding to reserve space for the overlay registration text (6px left pad + ~8px/char + 8px gap)
  const overlayRegPx = useRegOverlay ? Math.round(item._registration.length * 8) + 14 : 0
  const segExtGoRight = showSegExtReg && firstVisibleSeg
    ? firstVisibleSeg.startCol <= 7
    : true

  // Gray overlays for off-day columns that are not in the leading/trailing exception blocks
  const barStartInChunk = item.start_date && item.start_date >= chunkStart
  const barEndInChunk = item.end_date && item.end_date <= chunkEnd
  let barLeadingOff = 0
  if (barStartInChunk) {
    for (let c = bar.startCol; c <= bar.endCol; c++) {
      if (isOffDay(chunkDays[c])) barLeadingOff++
      else break
    }
  }
  let barTrailingOff = 0
  if (barEndInChunk) {
    for (let c = bar.endCol; c >= bar.startCol + barLeadingOff; c--) {
      if (isOffDay(chunkDays[c])) barTrailingOff++
      else break
    }
  }

  // Leading off-day columns in the first visible segment (for text shift).
  // Only count columns that actually have a gray overlay — skip leading/trailing
  // exception off-days (start_date/end_date on a weekend) which stay colored.
  let segLeadingOff = 0
  if (chunkDays && firstVisibleSeg) {
    for (let c = firstVisibleSeg.startCol; c <= firstVisibleSeg.endCol; c++) {
      const ri = c - bar.startCol
      if (isOffDay(chunkDays[c]) && ri >= barLeadingOff) segLeadingOff++
      else break
    }
  }
  const firstVisibleSegCols = firstVisibleSeg ? firstVisibleSeg.endCol - firstVisibleSeg.startCol + 1 : 0
  const segTextShiftPct = segLeadingOff > 0 && firstVisibleSeg && segLeadingOff < firstVisibleSegCols
    ? (segLeadingOff / firstVisibleSegCols) * 100
    : 0
  let trailingOff = 0
  if (chunkDays && item.end_date && item.end_date <= chunkEnd) {
    for (let c = bar.endCol; c >= bar.startCol; c--) {
      if (isOffDay(chunkDays[c])) trailingOff++
      else break
    }
  }
  const barGrayOverlays = []
  for (let c = bar.startCol; c <= bar.endCol; c++) {
    const ri = c - bar.startCol
    if (isOffDay(chunkDays[c]) && ri >= barLeadingOff && ri < bar.span - barTrailingOff) {
      barGrayOverlays.push(ri)
    }
  }

  return (
    <>
    <div
      className="absolute cursor-pointer hover:opacity-90 transition-opacity group"
      style={{
        left: `${bar.startCol * colWidth}%`,
        width: `${bar.span * colWidth}%`,
        top: bar.row * 32 + 4,
        height: 28,
        overflow: 'visible',
      }}
      onClick={() => onClickSubtask(item)}
    >
      {externalLabel}

      {showSegExtReg && firstVisibleSeg && (() => {
        const segStartPct = ((firstVisibleSeg.startCol - bar.startCol) / bar.span) * 100
        const segEndPct = ((firstVisibleSeg.endCol - bar.startCol + 1) / bar.span) * 100
        return (
          <div className="absolute top-0 bottom-0 flex items-center pointer-events-none z-20"
            style={{
              ...(segExtGoRight
                ? { left: `${segEndPct}%`, paddingLeft: 3 }
                : { right: `${100 - segStartPct}%`, paddingRight: 3, justifyContent: 'flex-end' }),
              whiteSpace: 'nowrap',
            }}
          >
            <span className={`text-xs font-bold bg-white/90 px-1 rounded shadow-sm ${isOverdue ? 'text-red-600' : 'text-gray-700'}`}>
              {item._registration}
            </span>
          </div>
        )
      })()}

      {/* Paint bucket — visible on hover, opens color picker */}
      {onColorChange && item._aircraftId && (
        <div
          className="absolute top-0 bottom-0 flex items-center z-20 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ right: trailingOff > 0 ? `calc(${(trailingOff / bar.span) * 100}% + 4px)` : '4px' }}
          onClick={openPicker}
        >
          <div className="w-4 h-4 rounded-full border-2 border-white shadow cursor-pointer"
            style={{ backgroundColor: item._color || '#9ca3af' }} />
        </div>
      )}
      {pickerOpen && pickerPos && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setPickerOpen(false)} />
          <BarColorPicker currentColor={item._color} position={pickerPos} onSave={handleColorSave} />
        </>,
        document.body
      )}

      {/* Registration overlay — outside the clipped segments container so it's never cut off */}
      {useRegOverlay && firstVisibleSeg && (
        <div
          className="absolute top-0 bottom-0 flex items-center pointer-events-none z-10"
          style={{
            left: `${((firstVisibleSeg.startCol - bar.startCol) / bar.span) * 100}%`,
            paddingLeft: segLeadingOff > 0 && segLeadingOff < bar.span ? `calc(${(segLeadingOff / bar.span) * 100}% + 6px)` : '6px',
          }}
        >
          <span
            className={`text-xs font-bold whitespace-nowrap ${
              firstVisibleSeg.isDifferentDept
                ? (isOverdue ? 'text-red-600' : 'text-gray-600')
                : (isOverdue ? 'text-red-300' : 'text-white')
            }`}
            style={!firstVisibleSeg.isDifferentDept ? { textShadow: '0 1px 3px rgba(0,0,0,0.4)' } : {}}
          >
            {item._registration}
          </span>
        </div>
      )}

      {/* Segments container — clips content */}
      <div className="w-full h-full rounded overflow-hidden relative">
        {(() => {
          return renderSegs.map((seg, i) => {
            if (hideOtherDept && seg.isDifferentDept) return null
            if (seg.isOffDayGap) return null

            const segLeft = ((seg.startCol - bar.startCol) / bar.span) * 100
            const segWidth = ((seg.endCol - seg.startCol + 1) / bar.span) * 100
            const segCols = seg.endCol - seg.startCol + 1
            const isFirst = i === firstVisibleIdx
            const showReg = isFirst && !isNarrow && !showSegExtReg && !useRegOverlay
            const showLabel = segCols >= 1
            const isNextAfterNarrow = showSegExtReg && segExtGoRight && i === firstVisibleIdx + 1

            // Trailing off-days within this segment — only count columns that have a
            // gray overlay; skip the bar's trailing exception off-days (end_date on weekend).
            let segTrailingOff = 0
            if (chunkDays) {
              for (let c = seg.endCol; c >= seg.startCol; c--) {
                const ri = c - bar.startCol
                if (isOffDay(chunkDays[c]) && ri < bar.span - barTrailingOff) segTrailingOff++
                else break
              }
            }
            const segTrailingOffPct = segTrailingOff > 0 && segTrailingOff < segCols ? (segTrailingOff / segCols) * 100 : 0

            // Leading off-days in non-first segments — shift text past gray overlays
            let segLeadingOffPct = 0
            if (!isFirst && chunkDays) {
              let segLeadingOff = 0
              for (let c = seg.startCol; c <= seg.endCol; c++) {
                if (isOffDay(chunkDays[c])) segLeadingOff++
                else break
              }
              if (segLeadingOff > 0 && segLeadingOff < segCols) {
                segLeadingOffPct = (segLeadingOff / segCols) * 100
              }
            }

            if (seg.isDifferentDept) {
              return (
                <div
                  key={i}
                  className="absolute top-0 bottom-0 flex items-center overflow-hidden px-1.5 gap-1"
                  style={{
                    left: `${segLeft}%`,
                    width: `${segWidth}%`,
                    backgroundColor: '#d1d5db',
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                    ...(isNextAfterNarrow ? { paddingLeft: 60 } : {}),
                    ...(isFirst && segTextShiftPct > 0 ? { paddingLeft: `calc(${segTextShiftPct}% + ${useRegOverlay ? `${overlayRegPx}px` : '6px'})` } : {}),
                    ...(isFirst && !segTextShiftPct && useRegOverlay ? { paddingLeft: overlayRegPx } : {}),
                    ...(!isNextAfterNarrow && segLeadingOffPct > 0 ? { paddingLeft: `calc(${segLeadingOffPct}% + 6px)` } : {}),
                    ...(segTrailingOff > 0 ? { paddingRight: `calc(${segTrailingOffPct}% + 6px)` } : {}),
                  }}
                >
                  {isFirst && effectiveHasExpand && !isNarrow && <ToggleChevron expanded={expanded} onToggle={onToggle} dark={true} />}
                  {showReg && (
                    <span className={`text-xs font-bold flex-shrink-0 ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}>
                      {item._registration}
                    </span>
                  )}
                  {showLabel && (
                    <div className="flex items-center gap-1 min-w-0">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: seg.deptColor, boxShadow: '0 0 0 1px rgba(255,255,255,0.6)' }}
                      />
                      <span className="text-xs text-gray-500 truncate">{seg.label}</span>
                    </div>
                  )}
                </div>
              )
            }

            return (
              <div
                key={i}
                className="absolute top-0 bottom-0 flex items-center overflow-hidden px-1.5 gap-1"
                style={{
                  left: `${segLeft}%`,
                  width: `${segWidth}%`,
                  backgroundColor: seg.color,
                  ...(seg.isSubtask ? { boxShadow: 'inset 0 0 0 1.5px rgba(255,255,255,0.4)' } : {}),
                  ...(isNextAfterNarrow ? { paddingLeft: 60 } : {}),
                  ...(isFirst && segTextShiftPct > 0 ? { paddingLeft: `calc(${segTextShiftPct}% + ${useRegOverlay ? `${overlayRegPx}px` : '6px'})` } : {}),
                  ...(isFirst && !segTextShiftPct && useRegOverlay ? { paddingLeft: overlayRegPx } : {}),
                  ...(!isNextAfterNarrow && segLeadingOffPct > 0 ? { paddingLeft: `calc(${segLeadingOffPct}% + 6px)` } : {}),
                  ...(segTrailingOff > 0 ? { paddingRight: `calc(${segTrailingOffPct}% + 6px)` } : {}),
                }}
              >
                {isFirst && effectiveHasExpand && !isNarrow && <ToggleChevron expanded={expanded} onToggle={onToggle} dark={false} />}
                {showReg && (
                  <span
                    className={`text-xs font-bold flex-shrink-0 ${isOverdue ? 'text-red-300' : 'text-white'}`}
                    style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}
                  >
                    {item._registration}
                  </span>
                )}
                {showLabel && (
                  <span
                    className="text-xs text-white/90 truncate min-w-0"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                  >
                    {seg.label}
                  </span>
                )}
              </div>
            )
          })
        })()}
        {barGrayOverlays.map(ri => (
          <div key={ri} className="absolute inset-y-0 pointer-events-none"
            style={{ left: `${(ri / bar.span) * 100}%`, width: `${(1 / bar.span) * 100}%`, backgroundColor: '#f3f4f6' }} />
        ))}
      </div>
    </div>
    </>
  )
}

// ── DEPT HEADER ───────────────────────────────────────────────────────────────

function DeptHeader({ depts, expandAll, onToggleExpandAll, hideOtherDept, onToggleHideOtherDept }) {
  const { profile } = useAuth()
  const orgDeptMap = useMemo(() => {
    const map = {}
    ;(profile?.orgs?.settings?.departments ?? []).forEach(d => { map[d.key] = d })
    return map
  }, [profile?.orgs?.id])

  const deptArray = [...depts]
  const title = deptArray.length === 1
    ? (orgDeptMap[deptArray[0]]?.label || deptArray[0])
    : deptArray.map(d => orgDeptMap[d]?.label || d).join(' + ')

  return (
    <div className="dept-header sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm px-5 py-3 flex items-center gap-4">
      <div className="flex gap-1 flex-shrink-0">
        {deptArray.map(dept => {
          const hex = orgDeptMap[dept]?.hex || '#9ca3af'
          return <div key={dept} className="w-3 h-10 rounded-full" style={{ backgroundColor: hex }} />
        })}
      </div>
      <div>
        <h2 className="text-xl font-bold text-gray-900 font-body">{title}</h2>
        <p className="text-sm text-gray-400 font-medium">Department Calendar</p>
      </div>
      <div className="ml-auto flex items-center gap-3 no-print">
  <button
    onClick={onToggleHideOtherDept}
    className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
      hideOtherDept ? 'bg-amber-50 border-amber-200 text-amber-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
    }`}
  >
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {hideOtherDept
        ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
      }
    </svg>
    {hideOtherDept ? 'Dept Work Only' : 'Hide Other Dept'}
  </button>
  <button
    onClick={onToggleExpandAll}
    className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
      expandAll ? 'bg-brand-red/10 border-brand-red/20 text-brand-red' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
    }`}
  >
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
    </svg>
    {expandAll ? 'Collapse All' : 'Expand All'}
  </button>
</div>
      </div>
  )
}

// ── MINI SUBTASK BAR ──────────────────────────────────────────────────────────

function MiniSubtaskBar({ subtask, cols, color, colWidth, topRow, onClick, chunkDays }) {
  const { startCol, span } = cols
  const isNarrow = span === 1

  const grayOverlays = []
  if (chunkDays) {
    const chunkStartISO = toISO(chunkDays[0])
    const chunkEndISO = toISO(chunkDays[chunkDays.length - 1])
    const startInChunk = subtask.start_date >= chunkStartISO
    const endInChunk = subtask.end_date <= chunkEndISO
    let leadingOff = 0
    if (startInChunk) {
      for (let c = startCol; c < startCol + span; c++) {
        if (isOffDay(chunkDays[c])) leadingOff++
        else break
      }
    }
    let trailingOff = 0
    if (endInChunk) {
      for (let c = startCol + span - 1; c >= startCol + leadingOff; c--) {
        if (isOffDay(chunkDays[c])) trailingOff++
        else break
      }
    }
    for (let c = startCol; c < startCol + span; c++) {
      const ri = c - startCol
      if (isOffDay(chunkDays[c]) && ri >= leadingOff && ri < span - trailingOff) {
        grayOverlays.push(ri)
      }
    }
  }

  return (
    <div
      className="absolute cursor-pointer hover:opacity-80 transition-opacity"
      style={{ left: `${startCol * colWidth}%`, width: `${span * colWidth}%`, top: topRow * 32 + 5, height: 24, overflow: 'visible' }}
      onClick={onClick}
    >
      {isNarrow && (
        <div className="absolute top-0 bottom-0 flex items-center pointer-events-none z-20 whitespace-nowrap"
          style={{ left: '100%', paddingLeft: 3 }}>
          <span className="text-xs text-gray-600 bg-white/90 px-1 rounded shadow-sm">{subtask.task_name}</span>
        </div>
      )}
      <div
        className="w-full h-full rounded-sm flex items-center overflow-hidden px-2 relative"
        style={{
          backgroundColor: color,
          opacity: 0.82,
          boxShadow: 'inset 2px 0 0 rgba(255,255,255,0.45)',
        }}
      >
        {!isNarrow && (
          <span className="text-xs text-white/90 truncate" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
            {subtask.task_name}
          </span>
        )}
        {grayOverlays.map(ri => (
          <div key={ri} className="absolute inset-y-0 pointer-events-none"
            style={{ left: `${(ri / span) * 100}%`, width: `${(1 / span) * 100}%`, backgroundColor: '#f3f4f6' }} />
        ))}
      </div>
    </div>
  )
}

// ── BAR LIST ──────────────────────────────────────────────────────────────────

function getSubtaskCols(subtask, chunkDays) {
  const chunkISOs = chunkDays.map(d => toISO(d))
  const chunkStart = chunkISOs[0]
  const chunkEnd = chunkISOs[chunkISOs.length - 1]
  if (!subtask.start_date || !subtask.end_date) return null
  if (subtask.end_date < chunkStart || subtask.start_date > chunkEnd) return null
  const startIdx = subtask.start_date < chunkStart ? 0 : chunkISOs.findIndex(iso => iso >= subtask.start_date)
  const endIdx = subtask.end_date > chunkEnd
    ? chunkISOs.length - 1
    : chunkISOs.reduce((last, iso, i) => iso <= subtask.end_date ? i : last, -1)
  if (startIdx === -1 || endIdx === -1 || startIdx > endIdx) return null
  return { startCol: startIdx, endCol: endIdx, span: endIdx - startIdx + 1 }
}

function BarList({ bars, chunkDays, onClickSubtask, items, todayISO, expandAll, hideOtherDept, onColorChange, isQAView }) {
  const { profile } = useAuth()
  const orgDeptMap = useMemo(() => Object.fromEntries((profile?.orgs?.settings?.departments ?? []).map(d => [d.key, d])), [profile?.orgs?.id])
  const colWidth = 100 / 14
  const [localExpanded, setLocalExpanded] = useState(new Set())
  const [localCollapsed, setLocalCollapsed] = useState(new Set())
  const [pistonPickerOpenId, setPistonPickerOpenId] = useState(null)
  const [pistonPickerPos, setPistonPickerPos] = useState(null)

  useEffect(() => {
    setLocalExpanded(new Set())
    setLocalCollapsed(new Set())
  }, [expandAll])

  const isExpanded = (projectId, hasExpand) => {
    if (!hasExpand) return false
    if (expandAll) return !localCollapsed.has(projectId)
    return localExpanded.has(projectId)
  }

  const toggleProject = (projectId) => {
    if (expandAll) {
      setLocalCollapsed(prev => { const n = new Set(prev); n.has(projectId) ? n.delete(projectId) : n.add(projectId); return n })
    } else {
      setLocalExpanded(prev => { const n = new Set(prev); n.has(projectId) ? n.delete(projectId) : n.add(projectId); return n })
    }
  }

  const effectiveBars = useMemo(() => {
    if (!hideOtherDept) return bars
    const filtered = bars.filter(bar =>
      bar.item._isLeadDept !== false ||
      bar.item._deptSubtasks?.some(s => getSubtaskCols(s, chunkDays) !== null)
    )
    return filtered.map((bar, i) => ({ ...bar, row: i }))
  }, [bars, hideOtherDept, chunkDays])

  const laidOut = useMemo(() => {
    let extra = 0
    return effectiveBars.map(bar => {
      const startY = bar.row + extra
      const sorted = bar.item._deptSubtasks || []
      const visibleInChunk = sorted.filter(s => getSubtaskCols(s, chunkDays) !== null)
      const hasExpandInChunk = visibleInChunk.length > 1
      const exp = isExpanded(bar.item.project_id, hasExpandInChunk)
      const visibleSubtasks = exp ? visibleInChunk : []
      extra += visibleSubtasks.length
      return { ...bar, startY, isExp: exp, _visibleSubtasks: visibleSubtasks, _hasExpandInChunk: hasExpandInChunk }
    })
  }, [effectiveBars, localExpanded, localCollapsed, expandAll, chunkDays])

  const totalRows = laidOut.length === 0 ? 1
    : Math.max(...laidOut.map(b => b.startY + 1 + (b._visibleSubtasks?.length || 0)))

  return (
    <>
    <div className="dept-barlist relative" style={{ minHeight: totalRows * 32 + 8 }}>
      {isQAView && (
        <div className="absolute inset-0 grid pointer-events-none" style={{ gridTemplateColumns: 'repeat(14, 1fr)' }}>
          {chunkDays.map((d, i) => (
            <div key={i} className={`border-r border-gray-100 last:border-r-0 ${isOffDay(d) ? 'bg-gray-50' : ''}`} />
          ))}
        </div>
      )}

      {chunkDays.some(d => toISO(d) === todayISO) && (() => {
        const idx = chunkDays.findIndex(d => toISO(d) === todayISO)
        return (
          <div className="absolute inset-y-0 w-0.5 bg-brand-red z-10 pointer-events-none"
            style={{ left: `${((idx + 0.5) / 14) * 100}%` }} />
        )
      })()}
      {!isQAView && (
        <div className="absolute inset-0 grid pointer-events-none" style={{ gridTemplateColumns: 'repeat(14, 1fr)' }}>
          {chunkDays.map((d, i) => (
            <div key={i} className={isOffDay(d) ? 'bg-gray-100' : ''} />
          ))}
        </div>
      )}

      {laidOut.map((bar, bi) => {
        if (bar.item._isPin) {
          return (
            <div key={bi} className="absolute cursor-pointer z-10"
              style={{ left: `${bar.startCol * colWidth + colWidth / 2}%`, top: bar.startY * 32 + 4, transform: 'translateX(-50%)' }}
              onClick={() => onClickSubtask(bar.item)}
            >
              <div className="flex flex-col items-center">
                <div className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full shadow-md whitespace-nowrap border border-yellow-500">
                  ⚠ {bar.item._registration}
                </div>
                <div className="w-0.5 h-2 bg-yellow-400" />
                <div className="w-2 h-2 bg-yellow-400 rotate-45" />
              </div>
            </div>
          )
        }

        const miniRows = bar._visibleSubtasks?.length > 0
          ? bar._visibleSubtasks.map((s, si) => {
              const cols = getSubtaskCols(s, chunkDays)
              return (
                <MiniSubtaskBar key={`m${bi}-${si}`} subtask={s} cols={cols} color={bar.item._color}
                  colWidth={colWidth} topRow={bar.startY + 1 + si} chunkDays={chunkDays}
                  onClick={() => onClickSubtask({ ...s, _registration: bar.item._registration })} />
              )
            })
          : null

        if (bar.item._segments) {
          return (
            <Fragment key={bi}>
              <SegmentedBar bar={{ ...bar, row: bar.startY }} colWidth={colWidth}
                onClickSubtask={onClickSubtask} chunkDays={chunkDays}
                expanded={bar.isExp} onToggle={() => toggleProject(bar.item.project_id)}
                hideOtherDept={hideOtherDept} hasExpand={bar._hasExpandInChunk}
                onColorChange={onColorChange} />
              {miniRows}
            </Fragment>
          )
        }

        const barColor = bar.item._color || orgDeptMap[bar.item.department]?.hex || '#9ca3af'
        const grayOverlays = []
        if (!isQAView) {
          const chunkStartISO = toISO(chunkDays[0])
          const chunkEndISO = toISO(chunkDays[chunkDays.length - 1])
          const startInChunk = bar.item.start_date && bar.item.start_date >= chunkStartISO
          const endInChunk = bar.item.end_date && bar.item.end_date <= chunkEndISO
          let leadingOff = 0
          if (startInChunk) {
            for (let c = bar.startCol; c < bar.startCol + bar.span; c++) {
              if (isOffDay(chunkDays[c])) leadingOff++
              else break
            }
          }
          let trailingOff = 0
          if (endInChunk) {
            for (let c = bar.startCol + bar.span - 1; c >= bar.startCol + leadingOff; c--) {
              if (isOffDay(chunkDays[c])) trailingOff++
              else break
            }
          }
          for (let c = bar.startCol; c < bar.startCol + bar.span; c++) {
            const ri = c - bar.startCol
            if (isOffDay(chunkDays[c]) && ri >= leadingOff && ri < bar.span - trailingOff) {
              grayOverlays.push(ri)
            }
          }
        }

        return (
          <Fragment key={bi}>
            <div
              className={`absolute cursor-pointer hover:opacity-80 transition-opacity group${bar.item._urgencyTier === 'done' ? ' opacity-50' : ''}`}
              style={{ left: `${bar.startCol * colWidth}%`, width: `${bar.span * colWidth}%`, top: bar.startY * 32 + 4, height: 28, overflow: 'visible' }}
              onClick={() => onClickSubtask(bar.item)}
            >
              {bar.item._isQARow && (
                <div className="absolute left-0 top-0 bottom-0 rounded-l pointer-events-none"
                  style={{ width: 4, backgroundColor: URGENCY_STRIPE[bar.item._urgencyTier] || '#9ca3af', zIndex: 2 }} />
              )}
              {bar.span <= 1 && (
                <div className="absolute top-0 bottom-0 flex items-center pointer-events-none z-20"
                  style={{ ...((bar.item._isQARow ? bar.startCol + bar.span >= 14 : bar.startCol !== 0) ? { right: '100%', paddingRight: 3, justifyContent: 'flex-end' } : { left: '100%', paddingLeft: 3 }), whiteSpace: 'nowrap' }}>
                  <span className={`text-xs font-bold bg-white/90 px-1 rounded shadow-sm ${bar.item.end_date && bar.item.end_date < new Date().toISOString().slice(0,10) && bar.item.status !== 'Complete' && !bar.item._allDeptComplete ? 'text-red-600' : 'text-gray-700'}`}>
                    {bar.item._isQARow ? bar.item._label : bar.item._registration}
                  </span>
                </div>
              )}
              <div className="w-full h-full rounded overflow-hidden relative"
                style={{ backgroundColor: barColor }}>
                <div className="absolute inset-y-0 flex items-center overflow-hidden gap-1"
                  style={{ left: 0, right: 0, paddingLeft: bar.item._isQARow ? 8 : 6, paddingRight: 6 }}>
                  {bar._hasExpandInChunk && bar.span > 1 && <ToggleChevron expanded={bar.isExp} onToggle={() => toggleProject(bar.item.project_id)} dark={false} />}
                  {(bar.span > 1 || bar.item._isQARow) && (
                    <span className={`text-xs font-bold flex-shrink-0 truncate ${bar.item.end_date && bar.item.end_date < new Date().toISOString().slice(0,10) && bar.item.status !== 'Complete' && !bar.item._allDeptComplete ? 'text-red-300' : 'text-white'}`} style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>{bar.item._registration}</span>
                  )}
                  {bar.span > 1 && bar.item._isQARow && (
                    <span className="text-xs text-white/90 truncate" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>— {bar.item._label}</span>
                  )}
                  {bar.span >= 1 && !bar.item._isQARow && (
                    <span className="text-xs text-white/90 truncate" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{bar.item._label}</span>
                  )}
                </div>
                {grayOverlays.map(ri => (
                  <div key={ri} className="absolute inset-y-0 pointer-events-none"
                    style={{ left: `${(ri / bar.span) * 100}%`, width: `${(1 / bar.span) * 100}%`, backgroundColor: '#f3f4f6' }} />
                ))}
              </div>
              {onColorChange && bar.item._aircraftId && (
                <div
                  className="absolute right-1 top-0 bottom-0 flex items-center z-20 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={e => {
                    e.stopPropagation()
                    const rect = e.currentTarget.getBoundingClientRect()
                    setPistonPickerPos({ top: rect.top, left: rect.left, right: window.innerWidth - rect.right })
                    setPistonPickerOpenId(bar.item.id)
                  }}
                >
                  <div className="w-4 h-4 rounded-full border-2 border-white shadow cursor-pointer"
                    style={{ backgroundColor: bar.item._color || '#9ca3af' }} />
                </div>
              )}
              {pistonPickerOpenId === bar.item.id && pistonPickerPos && createPortal(
                <>
                  <div className="fixed inset-0 z-[9998]" onClick={e => { e.stopPropagation(); setPistonPickerOpenId(null) }} />
                  <BarColorPicker
                    currentColor={bar.item._color}
                    position={pistonPickerPos}
                    onSave={async (color) => { setPistonPickerOpenId(null); await onColorChange(bar.item._aircraftId, color) }}
                  />
                </>,
                document.body
              )}
            </div>
            {miniRows}
          </Fragment>
        )
      })}
    </div>
    <div className="dept-print-barlist" style={{ position: 'relative' }}>
      {!isQAView && (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', pointerEvents: 'none', gridTemplateColumns: 'repeat(14, 1fr)' }}>
          {chunkDays.map((d, i) => (
            <div key={i} style={{ backgroundColor: isOffDay(d) ? '#f3f4f6' : 'transparent' }} />
          ))}
        </div>
      )}
      {laidOut.map((bar, bi) => {
        if (bar.item._isPin) return <div key={bi} style={{ height: 32 }} />
        const rowCount = 1 + (bar._visibleSubtasks?.length || 0)
        const color = bar.item._color || orgDeptMap[bar.item.department]?.hex || '#9ca3af'
        const grayOverlays = []
        if (!isQAView && !bar.item._segments) {
          const chunkStartISO = toISO(chunkDays[0])
          const chunkEndISO = toISO(chunkDays[chunkDays.length - 1])
          const startInChunk = bar.item.start_date && bar.item.start_date >= chunkStartISO
          const endInChunk = bar.item.end_date && bar.item.end_date <= chunkEndISO
          let leadingOff = 0
          if (startInChunk) {
            for (let c = bar.startCol; c < bar.startCol + bar.span; c++) {
              if (isOffDay(chunkDays[c])) leadingOff++
              else break
            }
          }
          let trailingOff = 0
          if (endInChunk) {
            for (let c = bar.startCol + bar.span - 1; c >= bar.startCol + leadingOff; c--) {
              if (isOffDay(chunkDays[c])) trailingOff++
              else break
            }
          }
          for (let c = bar.startCol; c < bar.startCol + bar.span; c++) {
            const ri = c - bar.startCol
            if (isOffDay(chunkDays[c]) && ri >= leadingOff && ri < bar.span - trailingOff) {
              grayOverlays.push(ri)
            }
          }
        }
        return (
          <div key={bi} className="dept-print-row-wrap" style={{ height: rowCount * 32, position: 'relative' }}>
            {bar.item._segments ? (
              <SegmentedBar
                bar={{ ...bar, row: 0 }}
                colWidth={colWidth}
                onClickSubtask={() => {}}
                chunkDays={chunkDays}
                expanded={bar.isExp}
                onToggle={() => {}}
                hideOtherDept={hideOtherDept}
                hasExpand={bar._hasExpandInChunk}
                onColorChange={null}
              />
            ) : (
              <Fragment>
                <div className="absolute rounded overflow-hidden" style={{
                  left: `${bar.startCol * colWidth}%`,
                  width: `${bar.span * colWidth}%`,
                  top: 4, height: 28,
                  backgroundColor: color,
                }}>
                  {bar.item._isQARow && (
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
                      borderRadius: '4px 0 0 4px',
                      backgroundColor: URGENCY_STRIPE[bar.item._urgencyTier] || '#9ca3af', zIndex: 2 }} />
                  )}
                  <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', gap: 4,
                    left: 0, paddingLeft: bar.item._isQARow ? 8 : 6, paddingRight: 6, overflow: 'hidden' }}>
                    {(bar.span > 1 || bar.item._isQARow) && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: 'white', flexShrink: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
                        {bar.item._registration}
                      </span>
                    )}
                    {bar.span >= 1 && !bar.item._isQARow && (
                      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.9)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                        {bar.item._label}
                      </span>
                    )}
                    {bar.span > 1 && bar.item._isQARow && (
                      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.9)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        — {bar.item._label}
                      </span>
                    )}
                  </div>
                  {grayOverlays.map(ri => (
                    <div key={ri} style={{ position: 'absolute', top: 0, bottom: 0, pointerEvents: 'none',
                      left: `${(ri / bar.span) * 100}%`, width: `${(1 / bar.span) * 100}%`, backgroundColor: '#f3f4f6' }} />
                  ))}
                </div>
              </Fragment>
            )}
            {bar._visibleSubtasks?.map((s, si) => {
              const cols = getSubtaskCols(s, chunkDays)
              if (!cols) return null
              const subChunkStartISO = toISO(chunkDays[0])
              const subChunkEndISO = toISO(chunkDays[chunkDays.length - 1])
              const subStartInChunk = s.start_date >= subChunkStartISO
              const subEndInChunk = s.end_date <= subChunkEndISO
              let subLeadingOff = 0
              if (subStartInChunk) {
                for (let c = cols.startCol; c < cols.startCol + cols.span; c++) {
                  if (isOffDay(chunkDays[c])) subLeadingOff++
                  else break
                }
              }
              let subTrailingOff = 0
              if (subEndInChunk) {
                for (let c = cols.startCol + cols.span - 1; c >= cols.startCol + subLeadingOff; c--) {
                  if (isOffDay(chunkDays[c])) subTrailingOff++
                  else break
                }
              }
              const subGrayOverlays = []
              for (let c = cols.startCol; c < cols.startCol + cols.span; c++) {
                const ri = c - cols.startCol
                if (isOffDay(chunkDays[c]) && ri >= subLeadingOff && ri < cols.span - subTrailingOff) {
                  subGrayOverlays.push(ri)
                }
              }
              return (
                <div key={si} className="absolute rounded overflow-hidden" style={{
                  left: `${cols.startCol * colWidth}%`,
                  width: `${cols.span * colWidth}%`,
                  top: (1 + si) * 32 + 4, height: 28,
                  backgroundColor: color, opacity: 0.8,
                }}>
                  <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0,
                    display: 'flex', alignItems: 'center', paddingLeft: 6, paddingRight: 6, overflow: 'hidden' }}>
                    <span style={{ fontSize: 10, color: 'white',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.task_name}
                    </span>
                  </div>
                  {subGrayOverlays.map(ri => (
                    <div key={ri} style={{ position: 'absolute', top: 0, bottom: 0, pointerEvents: 'none',
                      left: `${(ri / cols.span) * 100}%`, width: `${(1 / cols.span) * 100}%`, backgroundColor: '#f3f4f6' }} />
                  ))}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
    </>
  )
}

// ── COLUMN HEADERS ────────────────────────────────────────────────────────────

function ColumnHeaders({ chunkDays, todayISO, onDayClick, items, hideOtherDept }) {
  return (
    <div className="dept-col-headers grid border-b border-gray-200" style={{ gridTemplateColumns: 'repeat(14, 1fr)' }}>
      {chunkDays.map((d, i) => {
        const off = isOffDay(d)
        const isToday = toISO(d) === todayISO
        const iso = toISO(d)
        return (
          <div key={i}
            className={`border-r border-gray-200 last:border-r-0 px-1 py-1.5 text-center cursor-pointer hover:bg-brand-red/5 transition-colors ${off ? 'bg-gray-100' : 'bg-white'}`}
            onClick={(e) => {
              if (!onDayClick) return
              const subtasksOnDay = []
              const seenProjects = new Set()
              ;(items || []).forEach(item => {
                if (!item._isProjectBar) {
                  if (item.start_date <= iso && item.end_date >= iso) subtasksOnDay.push(item)
                  return
                }
                if (!item.start_date || item.start_date > iso || !item.end_date || item.end_date < iso) return
                const pid = item.project_id
                if (seenProjects.has(pid)) return

                const deptSubtasksOnDay = (item._deptSubtasks || []).filter(
                  s => s.start_date && s.end_date && s.start_date <= iso && s.end_date >= iso
                )

                if (hideOtherDept) {
                  const allSubtasksOnDay = (item._subtasks || []).filter(
                    s => s.start_date && s.end_date && s.start_date <= iso && s.end_date >= iso
                  )
                  // Exclude only if another dept is actively working this day and ours isn't
                  if (deptSubtasksOnDay.length === 0 && allSubtasksOnDay.length > 0) return
                  seenProjects.add(pid)
                  if (deptSubtasksOnDay.length > 0) {
                    deptSubtasksOnDay.forEach(s =>
                      subtasksOnDay.push({ ...s, _registration: item._registration })
                    )
                  } else {
                    subtasksOnDay.push({
                      id: `workscope-${pid}`, _registration: item._registration,
                      task_name: item._label || item._registration,
                      _isWorkscope: true, department: item.department, project_id: pid,
                    })
                  }
                } else {
                  seenProjects.add(pid)
                  let hasSubtasksOnDay = false
                  ;(item._subtasks || []).forEach(s => {
                    if (s.start_date && s.end_date && s.start_date <= iso && s.end_date >= iso) {
                      subtasksOnDay.push({ ...s, _registration: s._registration || item._registration })
                      hasSubtasksOnDay = true
                    }
                  })
                  if (!hasSubtasksOnDay) {
                    subtasksOnDay.push({
                      id: `workscope-${pid}`, _registration: item._registration,
                      task_name: item._label || item._registration,
                      _isWorkscope: true, department: item.department, project_id: pid,
                    })
                  }
                }
              })
              onDayClick(iso, subtasksOnDay, null, e.currentTarget.getBoundingClientRect())
            }}
          >
            <div className={`text-xs font-medium ${isToday ? 'text-brand-red' : off ? 'text-gray-400' : 'text-gray-500'}`}>
              {fmtDow(d)}
            </div>
            <div className={`text-xs font-bold ${isToday ? 'text-brand-red' : off ? 'text-gray-400' : 'text-gray-800'}`}>
              {fmtMd(d)}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── DEPT CHUNK ────────────────────────────────────────────────────────────────

function DeptChunk({ chunkDays, bars, onClickSubtask, onDayClick, items, expandAll, hideOtherDept, isCollapsed: controlledCollapsed, onToggle, onColorChange, isQAView }) {
  const [internalCollapsed, setInternalCollapsed] = useState(false)
  const controlled = onToggle !== undefined
  const collapsed = controlled ? controlledCollapsed : internalCollapsed
  const toggle = controlled ? onToggle : () => setInternalCollapsed(c => !c)
  const todayISO = toISO(new Date())
  const chunkStart = chunkDays[0]
  const chunkEnd = chunkDays[13]
  const isPast = toISO(chunkEnd) < todayISO

  return (
    <div className={`dept-chunk border-b border-gray-200 last:border-b-0 ${isPast ? 'opacity-70' : ''}`}>
      <div
        className="dept-chunk-header flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors select-none"
        onClick={toggle}
      >
        <div className="flex items-center gap-2">
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${collapsed ? '-rotate-90' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span className="text-xs font-semibold text-gray-600">
            {fmtMd(chunkStart)} — {fmtMd(chunkEnd)}
          </span>
          {isPast && <span className="text-xs text-gray-400 italic">past</span>}
        </div>
        <span className="text-xs text-gray-400">{collapsed ? 'Show' : 'Hide'}</span>
      </div>

      {!collapsed && (
        <div className="overflow-x-auto">
          <div style={{ minWidth: 700 }}>
            <ColumnHeaders chunkDays={chunkDays} todayISO={todayISO} onDayClick={onDayClick} items={items} hideOtherDept={hideOtherDept} />
            <BarList bars={bars} chunkDays={chunkDays} onClickSubtask={onClickSubtask} items={items} todayISO={todayISO} expandAll={expandAll} hideOtherDept={hideOtherDept} onColorChange={onColorChange} isQAView={isQAView} />
          </div>
        </div>
      )}
    </div>
  )
}

// ── PAST PERIODS GROUP ────────────────────────────────────────────────────────

function PastPeriodsGroup({ chunks, items, onClickSubtask, onDayClick, expandAll, hideOtherDept, onColorChange, isQAView }) {
  const [groupOpen, setGroupOpen] = useState(false)
  const [hasBeenOpened, setHasBeenOpened] = useState(false)
  // tracks which chunk indices are collapsed (controlled from here)
  const [collapsedSet, setCollapsedSet] = useState(new Set())

  const toggleGroup = () => {
    if (!hasBeenOpened) setHasBeenOpened(true)
    setGroupOpen(p => !p)
  }

  const toggleChunk = (idx) => {
    setCollapsedSet(prev => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }

  // When rolled up: show only chunks that are NOT collapsed (user kept them expanded).
  // Before the group has ever been opened, show nothing.
  const visibleIndices = groupOpen
    ? chunks.map((_, i) => i)
    : hasBeenOpened
      ? chunks.map((_, i) => i).filter(i => !collapsedSet.has(i))
      : []

  return (
    <div className="dept-past-group border-b border-gray-200">
      <div
        className="flex items-center justify-between px-4 py-2 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors select-none"
        onClick={toggleGroup}
      >
        <div className="flex items-center gap-2">
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${groupOpen ? '' : '-rotate-90'}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span className="text-xs font-semibold text-gray-500">Past Periods</span>
          <span className="text-xs text-gray-400">
            {toISO(chunks[0][0])} — {toISO(chunks[chunks.length - 1][13])}
          </span>
        </div>
        <span className="text-xs text-gray-400">{groupOpen ? 'Hide all' : 'Show all'}</span>
      </div>

      {visibleIndices.map(idx => {
        const chunkDays = chunks[idx]
        const bars = (isQAView ? assignQABars : assignBars)(items, chunkDays)
        return (
          <DeptChunk key={idx} chunkDays={chunkDays} bars={bars}
            onClickSubtask={onClickSubtask} onDayClick={onDayClick} items={items}
            expandAll={expandAll} hideOtherDept={hideOtherDept}
            isCollapsed={collapsedSet.has(idx)}
            onToggle={() => toggleChunk(idx)}
            onColorChange={onColorChange}
            isQAView={isQAView}
          />
        )
      })}
    </div>
  )
}

// ── FUTURE PERIODS GROUP ──────────────────────────────────────────────────────

function FuturePeriodsGroup({ chunks, items, onClickSubtask, onDayClick, expandAll, hideOtherDept, onColorChange, isQAView }) {
  const [showFuture, setShowFuture] = useState(true)

  return (
    <div className="dept-future-group border-b border-gray-200 last:border-b-0">
      <div
        className="dept-future-header flex items-center justify-between px-4 py-2 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors select-none"
        onClick={() => setShowFuture(p => !p)}
      >
        <div className="flex items-center gap-2">
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showFuture ? '' : '-rotate-90'}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span className="text-xs font-semibold text-gray-500">Future Periods</span>
          <span className="text-xs text-gray-400">
            {toISO(chunks[0][0])} — {toISO(chunks[chunks.length - 1][13])}
          </span>
        </div>
        <span className="text-xs text-gray-400">{showFuture ? 'Hide all' : 'Show all'}</span>
      </div>

      {showFuture && chunks.map((chunkDays, ci) => {
        const bars = (isQAView ? assignQABars : assignBars)(items, chunkDays)
        return (
          <DeptChunk key={ci} chunkDays={chunkDays} bars={bars}
            onClickSubtask={onClickSubtask} onDayClick={onDayClick} items={items} expandAll={expandAll} hideOtherDept={hideOtherDept} onColorChange={onColorChange} isQAView={isQAView} />
        )
      })}
    </div>
  )
}

// ── MONTHLY VIEW ──────────────────────────────────────────────────────────────

// qaFilter: technician ID string | null (null = All)
// projectTechMap: projectId → [techObjects] (union of project-level + subtask-level)
// qaTechs: all QA-department technician objects (used to detect "no QA tech assigned yet")
function applyQAFilter(items, qaFilter, projectTechMap, qaTechs) {
  if (!qaFilter) return items
  const qaTechIds = new Set(qaTechs.map(t => t.id))

  // Determine, per project, whether any QA tech is assigned at all
  const projectHasQATech = {}
  items.forEach(item => {
    if (item.project_id in projectHasQATech) return
    const techs = projectTechMap[item.project_id] || []
    projectHasQATech[item.project_id] = techs.some(t => qaTechIds.has(t.id))
  })

  return items.filter(item => {
    // No QA tech assigned to this project → show in every tech's view
    if (!projectHasQATech[item.project_id]) return true
    // Check if the selected tech is on this project
    const techs = projectTechMap[item.project_id] || []
    return techs.some(t => t.id === qaFilter)
  })
}

function MonthlyView({ depts, projects, subtasks, onClickSubtask, onDayClick, expandAll, hideOtherDept, onColorChange, qaFilter, projectTechMap, qaTechs }) {
  const { profile } = useAuth()
  const orgDeptMap = useMemo(() => Object.fromEntries((profile?.orgs?.settings?.departments ?? []).map(d => [d.key, d])), [profile?.orgs?.id])
  const weekStartDay = profile?.orgs?.settings?.scheduling?.weekStartDay ?? 1
  const todayISO = toISO(new Date())
  const isQAView = depts.size === 1 && depts.has('QA')

  const { pastChunks, currentChunk, futureChunks } = useMemo(() => {
    const jan1 = new Date(new Date().getFullYear(), 0, 1)
    jan1.setDate(jan1.getDate() - ((jan1.getDay() - weekStartDay + 7) % 7))
    const yearStart = jan1
    const yearEnd = addDays(new Date(), 365)
    const all = buildChunks(yearStart, yearEnd)
    return {
      pastChunks: all.filter(days => toISO(days[13]) < todayISO).slice(-3),
      currentChunk: all.find(days => toISO(days[0]) <= todayISO && toISO(days[13]) >= todayISO) || null,
      futureChunks: all.filter(days => toISO(days[0]) > todayISO),
    }
  }, [weekStartDay])

  const items = useMemo(() => {
    const visibleProjects = projects.filter(p => p.status !== 'TBD')
    const raw = isQAView
      ? buildQAItems(visibleProjects, subtasks)
      : [...depts].flatMap(dept => buildDeptItems(dept, visibleProjects, subtasks, orgDeptMap))
    return isQAView ? applyQAFilter(raw, qaFilter, projectTechMap, qaTechs) : raw
  }, [depts, projects, subtasks, isQAView, qaFilter, projectTechMap, qaTechs, orgDeptMap])

  return (
    <div id="dept-export">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {pastChunks.length > 0 && (
          <PastPeriodsGroup chunks={pastChunks} items={items} onClickSubtask={onClickSubtask} onDayClick={onDayClick} expandAll={expandAll} hideOtherDept={hideOtherDept} onColorChange={onColorChange} isQAView={isQAView} />
        )}
        {currentChunk && (() => {
          const bars = (isQAView ? assignQABars : assignBars)(items, currentChunk)
          return (
            <DeptChunk chunkDays={currentChunk} bars={bars}
              onClickSubtask={onClickSubtask} onDayClick={onDayClick} items={items} expandAll={expandAll} hideOtherDept={hideOtherDept} onColorChange={onColorChange} isQAView={isQAView} />
          )
        })()}
        {futureChunks.length > 0 && (
          <FuturePeriodsGroup chunks={futureChunks} items={items} onClickSubtask={onClickSubtask} onDayClick={onDayClick} expandAll={expandAll} hideOtherDept={hideOtherDept} onColorChange={onColorChange} isQAView={isQAView} />
        )}
      </div>
    </div>
  )
}

// ── WEEKLY VIEW ───────────────────────────────────────────────────────────────

function WeeklyView({ depts, projects, subtasks, onClickSubtask, onDayClick, expandAll, hideOtherDept, onColorChange, qaFilter, projectTechMap, qaTechs }) {
  const { profile } = useAuth()
  const orgDeptMap = useMemo(() => Object.fromEntries((profile?.orgs?.settings?.departments ?? []).map(d => [d.key, d])), [profile?.orgs?.id])
  const weekStartDay = profile?.orgs?.settings?.scheduling?.weekStartDay ?? 1
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const isQAView = depts.size === 1 && depts.has('QA')
  const todayISO = toISO(today)

  const [chunkStart, setChunkStart] = useState(() => {
    const d = new Date(today)
    d.setDate(d.getDate() - ((d.getDay() - weekStartDay + 7) % 7))
    return d
  })

  const chunkDays = useMemo(() =>
    Array.from({ length: 14 }, (_, i) => addDays(chunkStart, i)), [chunkStart])

  const navigate = (dir) => setChunkStart(prev => addDays(prev, dir * 7))
  const goToday = () => {
    const d = new Date(today)
    d.setDate(d.getDate() - ((d.getDay() - weekStartDay + 7) % 7))
    setChunkStart(d)
  }

  const items = useMemo(() => {
    const visibleProjects = projects.filter(p => p.status !== 'TBD')
    const raw = isQAView
      ? buildQAItems(visibleProjects, subtasks)
      : [...depts].flatMap(dept => buildDeptItems(dept, visibleProjects, subtasks, orgDeptMap))
    return isQAView ? applyQAFilter(raw, qaFilter, projectTechMap, qaTechs) : raw
  }, [depts, projects, subtasks, isQAView, qaFilter, projectTechMap, qaTechs, orgDeptMap])
  const bars = useMemo(() => (isQAView ? assignQABars : assignBars)(items, chunkDays), [items, chunkDays, isQAView])

  return (
    <div id="dept-export">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="no-print flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <span className="text-sm font-semibold text-gray-600">
            {fmtMd(chunkDays[0])} — {fmtMd(chunkDays[13])}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate(-1)} className="p-1.5 rounded hover:bg-gray-200 transition-colors text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button onClick={goToday} className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 transition-colors">
              Today
            </button>
            <button onClick={() => navigate(1)} className="p-1.5 rounded hover:bg-gray-200 transition-colors text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div style={{ minWidth: 700 }}>
            <ColumnHeaders chunkDays={chunkDays} todayISO={todayISO} onDayClick={onDayClick} items={items} hideOtherDept={hideOtherDept} />
            <BarList bars={bars} chunkDays={chunkDays} onClickSubtask={onClickSubtask} items={items} todayISO={todayISO} expandAll={expandAll} hideOtherDept={hideOtherDept} onColorChange={onColorChange} isQAView={isQAView} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ── HANGARS VIEW ─────────────────────────────────────────────────────────────

function HangarsView({ activeHangars, projects, subtasks, onClickSubtask, onDayClick, expandAll, onColorChange }) {
  const todayISO = toISO(new Date())
  const { profile } = useAuth()
  const weekStartDay = profile?.orgs?.settings?.scheduling?.weekStartDay ?? 1

  const { pastChunks, currentChunk, futureChunks } = useMemo(() => {
    const jan1 = new Date(new Date().getFullYear(), 0, 1)
    jan1.setDate(jan1.getDate() - ((jan1.getDay() - weekStartDay + 7) % 7))
    const yearEnd = addDays(new Date(), 365)
    const all = buildChunks(jan1, yearEnd)
    return {
      pastChunks: all.filter(days => toISO(days[13]) < todayISO).slice(-3),
      currentChunk: all.find(days => toISO(days[0]) <= todayISO && toISO(days[13]) >= todayISO) || null,
      futureChunks: all.filter(days => toISO(days[0]) > todayISO),
    }
  }, [weekStartDay])

  const effectiveHangars = useMemo(() => {
    if (activeHangars.size > 0) return activeHangars
    const set = new Set()
    projects.forEach(p => { if (p.hangar_location) set.add(p.hangar_location.trim().toUpperCase()) })
    return set
  }, [activeHangars, projects])

  const items = useMemo(() => {
    if (!effectiveHangars.size) return []
    return buildHangarItems(effectiveHangars, projects.filter(p => p.status !== 'TBD'), subtasks)
  }, [effectiveHangars, projects, subtasks])

  if (!effectiveHangars.size) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
        No projects with location data
      </div>
    )
  }

  return (
    <div id="dept-export">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {pastChunks.length > 0 && (
          <PastPeriodsGroup chunks={pastChunks} items={items} onClickSubtask={onClickSubtask} onDayClick={onDayClick}
            expandAll={expandAll} hideOtherDept={false} onColorChange={onColorChange} isQAView={false} />
        )}
        {currentChunk && (() => {
          const bars = assignBars(items, currentChunk)
          return (
            <DeptChunk chunkDays={currentChunk} bars={bars} onClickSubtask={onClickSubtask} onDayClick={onDayClick}
              items={items} expandAll={expandAll} hideOtherDept={false} onColorChange={onColorChange} isQAView={false} />
          )
        })()}
        {futureChunks.length > 0 && (
          <FuturePeriodsGroup chunks={futureChunks} items={items} onClickSubtask={onClickSubtask} onDayClick={onDayClick}
            expandAll={expandAll} hideOtherDept={false} onColorChange={onColorChange} isQAView={false} />
        )}
      </div>
    </div>
  )
}

// ── MAIN ──────────────────────────────────────────────────────────────────────

export default function DepartmentCalendarView({ projects, subtasks, subView, onClickSubtask, refetch, isEditor, onAddProject, userProfile, projectTechMap = {}, technicians = [], lockedDept = null, lockedQAFilter = null }) {
  const { profile } = useAuth()
  const orgDepts = profile?.orgs?.settings?.departments ?? []
  const orgLocations = profile?.orgs?.settings?.locations ?? []
  const hangarLabelMap = useMemo(() => {
    const map = {}
    orgLocations.forEach(l => { map[l.key] = l.label })
    return map
  }, [orgLocations])

  const [activeDepts, setActiveDepts] = useState(() => {
    if (lockedDept) return new Set([lockedDept])
    const validKeys = new Set(orgDepts.map(d => d.key))
    const saved = sessionStorage.getItem('deptCalActiveDepts')
    if (saved) {
      const filtered = JSON.parse(saved).filter(k => validKeys.has(k))
      if (filtered.length > 0) return new Set(filtered)
    }
    return orgDepts[0] ? new Set([orgDepts[0].key]) : new Set()
  })

  // After profile loads, correct any stale activeDepts that aren't valid for this org
  const orgId = profile?.orgs?.id
  useEffect(() => {
    if (lockedDept || !orgId) return
    const validKeys = new Set(orgDepts.map(d => d.key))
    if (validKeys.size === 0) return
    setActiveDepts(prev => {
      const valid = [...prev].filter(k => validKeys.has(k))
      if (valid.length > 0) return prev
      const next = new Set([orgDepts[0].key])
      sessionStorage.setItem('deptCalActiveDepts', JSON.stringify([...next]))
      return next
    })
  }, [orgId]) // eslint-disable-line react-hooks/exhaustive-deps

  const [expandAll, setExpandAll] = useState(false)
  const [hideOtherDept, setHideOtherDept] = useState(true)
  const [dayPicker, setDayPicker] = useState(null)
  const [subtaskModal, setSubtaskModal] = useState(null)
  const [qaFilter, setQAFilter] = useState(lockedQAFilter)
  const [activeHangars, setActiveHangars] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('deptCalActiveHangars') || '[]')) } catch { return new Set() }
  })
  const [hangarSearch, setHangarSearch] = useState('')
  const [hangarDropdownOpen, setHangarDropdownOpen] = useState(false)
  const hangarDropdownRef = useRef(null)

  const orgAircraftTypes = profile?.orgs?.settings?.aircraftTypes ?? ['Jet', 'Piston']
  const [activeTypes, setActiveTypes] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('deptCalActiveTypes') || '[]')) } catch { return new Set() }
  })

  const availableHangars = useMemo(() => {
    if (orgLocations.length > 0) return orgLocations.map(l => l.key)
    const set = new Set()
    projects.forEach(p => { if (p.hangar_location) set.add(p.hangar_location.trim().toUpperCase()) })
    return [...set].sort((a, b) => {
      const numA = parseInt(a), numB = parseInt(b)
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB
      return a.localeCompare(b)
    })
  }, [projects, orgLocations])

  const isQADept = activeDepts.size === 1 && activeDepts.has('QA')
  const qaTechs = useMemo(() =>
    technicians.filter(t => t.department === 'QA').sort((a, b) => a.last_name.localeCompare(b.last_name)),
    [technicians]
  )

  const toggleDept = (dept) => {
    const next = new Set([dept])
    sessionStorage.setItem('deptCalActiveDepts', JSON.stringify([...next]))
    setActiveDepts(next)
    setQAFilter(null)
  }

  const toggleType = (type) => {
    setActiveTypes(prev => {
      const next = new Set(prev)
      next.has(type) ? next.delete(type) : next.add(type)
      sessionStorage.setItem('deptCalActiveTypes', JSON.stringify([...next]))
      return next
    })
  }

  const typeFilteredProjects = useMemo(() => {
    if (activeTypes.size === 0) return projects
    return projects.filter(p => activeTypes.has(p.aircraft?.aircraft_type))
  }, [projects, activeTypes])

  const openDayPicker = (date, subtasksOnDay, projectId, anchorRect) => {
    setDayPicker({ date, subtasks: subtasksOnDay, projectId, anchorRect })
  }

  const handleColorChange = async (aircraftId, color) => {
    if (!aircraftId) return
    await updateAircraft(aircraftId, { color: color ?? null })
    refetch()
  }

  return (
    <div>
      {subView === 'ac-type' ? (
        <>
          {/* Aircraft type filter pills */}
          <div className="flex items-center gap-1 mb-2 flex-wrap no-print">
            {orgAircraftTypes.map(type => {
              const isActive = activeTypes.has(type)
              return (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    isActive
                      ? 'bg-gray-900 text-white border-transparent'
                      : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  {type}
                </button>
              )
            })}
            {activeTypes.size > 0 && (
              <button
                onClick={() => { setActiveTypes(new Set()); sessionStorage.removeItem('deptCalActiveTypes') }}
                className="px-2.5 py-1.5 rounded-lg text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Clear
              </button>
            )}
          </div>

          {/* Export button */}
          <div className="flex justify-end mb-4 no-print">
            <ExportButton
              elementId="dept-export"
              filename={`ac-type-${activeTypes.size ? [...activeTypes].sort().join('-') : 'all'}-${new Date().toISOString().slice(0, 10)}`}
              showTechView={false}
            />
          </div>

          {/* Sticky header */}
          <div className="dept-header sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm px-5 py-3 flex items-center gap-4 mb-4">
            <div className="w-3 h-10 rounded-full bg-gray-700 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 font-body">
                {activeTypes.size ? [...activeTypes].join(' · ') : 'All Types'}
              </h2>
              <p className="text-sm text-gray-400 font-medium">AC Type Calendar</p>
            </div>
            <div className="ml-auto flex items-center gap-3 no-print">
              <button
                onClick={() => setExpandAll(v => !v)}
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                  expandAll ? 'bg-brand-red/10 border-brand-red/20 text-brand-red' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                {expandAll ? 'Collapse All' : 'Expand All'}
              </button>
            </div>
          </div>

          {/* Calendar — project-level bars, filtered by type */}
          <div className="mt-4 dept-cal-content">
            <AcTypeView
              key={[...activeTypes].sort().join(',')}
              projects={typeFilteredProjects}
              subtasks={subtasks}
              onClickSubtask={onClickSubtask}
              onDayClick={openDayPicker}
              expandAll={expandAll}
              onColorChange={handleColorChange}
            />
          </div>
        </>
      ) : subView === 'hangars' ? (
        <>
          {/* Hangar filter dropdown */}
          <div className="flex items-center gap-2 mb-2 no-print" ref={hangarDropdownRef}>
            <div className="relative">
              <button
                onClick={() => setHangarDropdownOpen(v => !v)}
                className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                </svg>
                <span className="text-gray-600 font-medium">
                  {activeHangars.size > 0 ? `${activeHangars.size} selected` : 'All locations'}
                </span>
                <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${hangarDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {hangarDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg min-w-[200px] py-1"
                  onMouseLeave={() => setHangarDropdownOpen(false)}
                >
                  <div className="px-3 py-2 border-b border-gray-100">
                    <input
                      type="text"
                      value={hangarSearch}
                      onChange={e => setHangarSearch(e.target.value)}
                      placeholder="Search…"
                      className="w-full text-sm px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-red/20"
                      autoFocus
                    />
                  </div>
                  {activeHangars.size > 0 && (
                    <button
                      className="w-full text-left px-3 py-1.5 text-xs text-brand-red hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        setActiveHangars(new Set())
                        sessionStorage.setItem('deptCalActiveHangars', '[]')
                      }}
                    >
                      Clear selection
                    </button>
                  )}
                  <div className="max-h-60 overflow-y-auto">
                    {availableHangars
                      .filter(h => {
                        if (!hangarSearch) return true
                        const lc = hangarSearch.toLowerCase()
                        return h.toLowerCase().includes(lc) || (hangarLabelMap[h] ?? '').toLowerCase().includes(lc)
                      })
                      .map(h => (
                        <label key={h} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={activeHangars.has(h)}
                            onChange={() => {
                              setActiveHangars(prev => {
                                const next = new Set(prev)
                                next.has(h) ? next.delete(h) : next.add(h)
                                sessionStorage.setItem('deptCalActiveHangars', JSON.stringify([...next]))
                                return next
                              })
                            }}
                            className="rounded border-gray-300 text-brand-red focus:ring-brand-red/30"
                          />
                          <span className="text-sm text-gray-700">{hangarLabelMap[h] ?? `Hangar ${h}`}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Export button */}
          <div className="flex justify-end mb-4 no-print">
            <ExportButton
              elementId="dept-export"
              filename={`hangar-${activeHangars.size ? [...activeHangars].sort().join('-') : 'calendar'}-${new Date().toISOString().slice(0, 10)}`}
              showTechView={false}
            />
          </div>

          {/* Hangar sticky header */}
          <div className="dept-header sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm px-5 py-3 flex items-center gap-4 mb-4">
            <div className="w-3 h-10 rounded-full bg-brand-red flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 font-body">
                {activeHangars.size ? [...activeHangars].sort().map(h => hangarLabelMap[h] ?? `Hangar ${h}`).join(', ') : 'Hangars'}
              </h2>
              <p className="text-sm text-gray-400 font-medium">Hangar Calendar</p>
            </div>
            <div className="ml-auto flex items-center gap-3 no-print">
              <button
                onClick={() => setExpandAll(v => !v)}
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                  expandAll ? 'bg-brand-red/10 border-brand-red/20 text-brand-red' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                {expandAll ? 'Collapse All' : 'Expand All'}
              </button>
            </div>
          </div>

          {/* Hangar calendar */}
          <div className="mt-4 dept-cal-content">
            <HangarsView key={[...activeHangars].sort().join(',')} activeHangars={activeHangars}
              projects={projects} subtasks={subtasks}
              onClickSubtask={onClickSubtask} onDayClick={openDayPicker}
              expandAll={expandAll} onColorChange={handleColorChange} />
          </div>
        </>
      ) : (
        <>
          {/* Dept tabs — hidden when a department is locked (e.g. QA dashboard view) */}
          {!lockedDept && (
            <div className="flex items-center gap-1 mb-2 flex-wrap no-print">
              {orgDepts.map(({ key: dept, label: deptLabel, hex: deptHex }) => {
                const isActive = activeDepts.has(dept)
                return (
                  <button key={dept} onClick={() => toggleDept(dept)}
                    style={isActive ? { backgroundColor: deptHex + '20', color: deptHex, borderColor: 'transparent' } : {}}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                      isActive ? 'border-transparent' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: deptHex }} />
                    {deptLabel ?? dept}
                  </button>
                )
              })}
            </div>
          )}

          {/* Export button */}
          <div className="flex justify-end mb-4 no-print">
            <ExportButton
              elementId="dept-export"
              filename={`${[...activeDepts].join('-')}-calendar-${new Date().toISOString().slice(0, 10)}`}
              showTechView={false}
            />
          </div>

          {/* Sticky dept header */}
          <DeptHeader depts={activeDepts} expandAll={expandAll} onToggleExpandAll={() => setExpandAll(v => !v)}
            hideOtherDept={hideOtherDept} onToggleHideOtherDept={() => setHideOtherDept(v => !v)} />

          {/* QA tech filter — hidden when auto-filtered via lockedQAFilter */}
          {isQADept && qaTechs.length > 0 && !lockedQAFilter && (
            <div className="flex items-center gap-2 mt-3 mb-1 no-print flex-wrap">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Filter:</span>
              {[{ id: null, label: 'All' }, ...qaTechs.map(t => ({ id: t.id, label: `${t.first_name} ${t.last_name}` }))].map(opt => (
                <button
                  key={opt.id ?? 'all'}
                  onClick={() => setQAFilter(opt.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    qaFilter === opt.id
                      ? 'bg-yellow-100 text-yellow-800 border-transparent'
                      : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* Content */}
          <div className="mt-4 dept-cal-content">
            {subView === 'monthly' ? (
              <MonthlyView key={[...activeDepts].join(',')} depts={activeDepts}
                projects={projects} subtasks={subtasks}
                onClickSubtask={onClickSubtask} onDayClick={openDayPicker} expandAll={expandAll} hideOtherDept={hideOtherDept}
                onColorChange={handleColorChange} qaFilter={qaFilter} projectTechMap={projectTechMap} qaTechs={qaTechs} />
            ) : (
              <WeeklyView key={[...activeDepts].join(',')} depts={activeDepts}
                projects={projects} subtasks={subtasks}
                onClickSubtask={onClickSubtask} onDayClick={openDayPicker} expandAll={expandAll} hideOtherDept={hideOtherDept}
                onColorChange={handleColorChange} qaFilter={qaFilter} projectTechMap={projectTechMap} qaTechs={qaTechs} />
            )}
          </div>
        </>
      )}

      {/* Day picker popover */}
      {dayPicker && (
        <DayPickerPopover
          date={dayPicker.date}
          subtasks={dayPicker.subtasks}
          anchorRect={dayPicker.anchorRect}
          groupBy="registration"
          onClose={() => setDayPicker(null)}
          onEditSubtask={isEditor ? (s) => {
            setDayPicker(null)
            setSubtaskModal({ projectId: s.project_id, subtask: s })
          } : undefined}
          onDeleteSubtask={isEditor ? async (s) => {
            if (!confirm(`Delete "${s.task_name}"?`)) return
            await logChange({
              projectId: s.project_id, entityType: 'subtask', entityId: s.id,
              entityLabel: s.task_name, action: 'deleted', userProfile,
            })
            await deleteSubtask(s.id)
            setDayPicker(null)
            refetch()
          } : undefined}
          onAddSubtask={isEditor && onAddProject ? () => {
            setDayPicker(null)
            onAddProject()
          } : undefined}
        />
      )}

      {/* Subtask modal */}
      {subtaskModal && (
        <SubtaskModal
          projectId={subtaskModal.projectId}
          subtask={subtaskModal.subtask}
          defaultStartDate={subtaskModal.defaultStartDate}
          defaultEndDate={subtaskModal.defaultEndDate}
          onClose={() => setSubtaskModal(null)}
          onSaved={() => { setSubtaskModal(null); refetch() }}
        />
      )}
    </div>
  )
}